<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMediaQuery } from '@vueuse/core'
import { LogOut, Menu } from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import NotificationBell from '@/components/umisis/notifications/NotificationBell.vue'
import RecursiveNav from '@/components/umisis/navigation/RecursiveNav.vue'
import { useAuthStore } from '@/stores/auth.js'
import { useLeftMenuStore } from '@/stores/leftMenuStore.js'
import CentratelLogo from '../CentratelLogo.vue'
import LogoLink from '@/components/LogoLink.vue'

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const isSigningOut = ref(false)
const isMobileMenuOpen = ref(false)
const isDesktop = useMediaQuery('(min-width: 768px)')
const menuStore = useLeftMenuStore()
const packageMenu = computed(() => menuStore.sortedMenu.find(item => item.id === 'holidays-public'))

function closeMobileMenu() {
  isMobileMenuOpen.value = false
}

watch([isDesktop, () => route.fullPath], closeMobileMenu)

async function signOut() {
  if (isSigningOut.value) {
    return
  }

  isSigningOut.value = true

  try {
    await authStore.logout()
    await router.replace({ name: 'Login' })
  } catch {
    toast.error('Unable to sign out. Please try again.')
  } finally {
    isSigningOut.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
    <div class="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">
      <header class="flex items-center justify-between gap-3">
        <LogoLink>
          <CentratelLogo location="navigation" class="max-w-32 sm:max-w-40" />
        </LogoLink>
        <div class="flex shrink-0 items-center gap-2 sm:gap-3">
          <NotificationBell />
          <Button type="button" variant="outline" class="px-2 sm:px-4" :loading="isSigningOut" :aria-label="$t('userMenu.logout')" @click="signOut">
            <LogOut class="h-4 w-4" />
            <span class="sr-only sm:not-sr-only">{{ $t('userMenu.logout') }}</span>
            <template #loading>
              <span class="sr-only sm:not-sr-only">{{ $t('userMenu.logout') }}</span>
            </template>
          </Button>
          <Sheet v-if="packageMenu?.children.length" v-model:open="isMobileMenuOpen">
            <SheetTrigger as-child>
              <Button type="button" variant="outline" size="icon" class="md:hidden" :aria-label="$t('holidaysPublic.nav.openMenu')">
                <Menu class="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" class="flex flex-col gap-6 overflow-y-auto">
              <SheetHeader>
                <SheetTitle>{{ $t(packageMenu.title) }}</SheetTitle>
                <SheetDescription class="sr-only">
                  {{ $t('holidaysPublic.nav.menuDescription') }}
                </SheetDescription>
              </SheetHeader>
              <nav :aria-label="$t(packageMenu.title)">
                <RecursiveNav :items="packageMenu.children" @navigate="closeMobileMenu" />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <nav v-if="packageMenu?.children.length" :aria-label="$t(packageMenu.title)" class="hidden md:block">
        <RecursiveNav :items="packageMenu.children" orientation="horizontal" />
      </nav>

      <main class="min-w-0 rounded-2xl border bg-card p-4 text-card-foreground shadow-sm sm:p-6 lg:p-8">
        <router-view />
      </main>
    </div>
  </div>
</template>
